import { StatusCodes } from 'http-status-codes'

import {
  getBackendErrorMessage,
  SEAT_LIMIT_EXCEEDED_CODE,
} from '@/features/users/userErrors'

/**
 * One thing wrong with an uploaded file.
 *
 * `source_line_number` is the line the admin would count to in their own file,
 * counting the header — `null` when the problem is with the file as a whole rather
 * than with one row. It is the backend's number and must be shown as given: the
 * whole point of reporting it is that the admin can find the row in the file they
 * prepared, which renumbering or re-sorting defeats.
 */
export interface BulkImportProblem {
  source_line_number: number | null
  column: string | null
  message: string
  /**
   * A stable identifier for the problems a client has to tell apart from "fix your
   * file". `null` for ordinary row problems, whose only sensible response is editing
   * the file.
   */
  code: string | null
}

/**
 * Everything the backend said about a refused upload, in one shape.
 *
 * A file can be refused two ways and this deliberately flattens them, because the
 * admin's question is the same either way — *what is wrong, and did anything happen?*
 * A `422` carries every problem found in the file at once; the admission failures
 * (not an admin, account not ready, federated realm, federation unreadable, file too
 * large) are single whole-file problems with their own status codes. Both arrive here
 * as `problems`, and whole-file ones are the entries with no line number.
 */
export interface BulkImportRejection {
  /**
   * The envelope's own human-readable summary, when it is worth showing above the
   * list. Absent when it would repeat the only problem, and absent for a seat-only
   * refusal — see `seatLimitOnly`.
   */
  message?: string
  problems: BulkImportProblem[]
  /**
   * Every problem is a seat refusal, so the file itself is fine.
   *
   * The backend's envelope hardcodes *"The uploaded file has N problem(s) and no users
   * were created. Fix them and upload the file again."*, which is wrong advice for a
   * clean file refused because the account has too few seats free — there is nothing in
   * it to fix. The message is shared by every validation failure, so correcting it
   * backend-side needs a case rather than a reword; until then this flag is what lets
   * the UI drop it.
   */
  seatLimitOnly: boolean
}

interface RejectionBody {
  message?: unknown
  extra?: {
    code?: unknown
    errors?: unknown
  }
}

/** Narrows an RTK Query rejection to its status and parsed body. */
function asFetchError(
  error: unknown,
): { status: number; data: RejectionBody } | undefined {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('status' in error) ||
    !('data' in error)
  ) {
    return undefined
  }
  // RTK Query's unwrap() rejects with a plain object; the checks above narrow it.
  const { status, data } = error as { status: unknown; data: unknown }
  if (typeof status !== 'number') return undefined
  if (typeof data !== 'object' || data === null) return undefined
  return { status, data: data as RejectionBody }
}

// What GIM's exception handler puts in `message` for a DRF field-validation failure.
// Mirrors the constant in userErrors.ts, which keeps it private.
const GENERIC_VALIDATION_MESSAGE = 'Validation error'

/**
 * The message the backend wrote for the admin, for the one status where
 * `getBackendErrorMessage` will not give it up.
 *
 * That helper excludes every 5xx on the grounds that their messages describe
 * internals — true of an unhandled 500, and wrong for the 503 this endpoint raises on
 * purpose: admission control answers "could not check how users on this account sign
 * in, so the import has not been started" that way, and it is the only account of
 * itself the admin gets. Narrowed to 503 rather than widening the shared helper,
 * which speaks for the single-user screens too.
 */
function serviceUnavailableMessage(data: RejectionBody): string | undefined {
  if (typeof data.message !== 'string') return undefined
  const trimmed = data.message.trim()
  return trimmed && trimmed !== GENERIC_VALIDATION_MESSAGE ? trimmed : undefined
}

function asProblem(candidate: unknown): BulkImportProblem | undefined {
  if (typeof candidate !== 'object' || candidate === null) return undefined
  const entry = candidate as Record<string, unknown>
  if (typeof entry.message !== 'string') return undefined
  return {
    source_line_number:
      typeof entry.source_line_number === 'number'
        ? entry.source_line_number
        : null,
    column: typeof entry.column === 'string' ? entry.column : null,
    message: entry.message,
    code: typeof entry.code === 'string' ? entry.code : null,
  }
}

/**
 * What the backend said about a refused upload, or `undefined` when it said nothing
 * usable — a 5xx, a network failure, or a body in a shape this does not recognise.
 * The caller shows its own generic message in that case rather than inventing detail.
 *
 * The per-problem `code` sits at `extra.errors[i].code`, not at `extra.code` where the
 * single-user create path puts it, so `hasApplicationErrorCode` in `userErrors.ts` does
 * not apply here. The same identifiers are reused; the lookup is not shareable.
 */
export function getBulkImportRejection(
  error: unknown,
): BulkImportRejection | undefined {
  const fetchError = asFetchError(error)
  if (!fetchError) return undefined

  const { status, data } = fetchError
  const envelope =
    status === StatusCodes.SERVICE_UNAVAILABLE
      ? serviceUnavailableMessage(data)
      : getBackendErrorMessage(error)

  if (status === StatusCodes.UNPROCESSABLE_ENTITY) {
    const raw = data.extra?.errors
    if (Array.isArray(raw)) {
      const problems = raw
        .map(asProblem)
        .filter(
          (problem): problem is BulkImportProblem => problem !== undefined,
        )
      if (problems.length) {
        const seatLimitOnly = problems.every(
          problem => problem.code === SEAT_LIMIT_EXCEEDED_CODE,
        )
        return {
          message: seatLimitOnly ? undefined : envelope,
          problems,
          seatLimitOnly,
        }
      }
    }
  }

  // Every other refusal the backend states is a single fact about the whole file, and
  // it states it in `message`. Presented as one whole-file problem so a rejected upload
  // reads the same way whichever check refused it. No message text is matched on: the
  // backend writes these for the end user, including the federated-realm one, which
  // already explains itself as a limitation rather than as something to fix.
  if (envelope) {
    return {
      problems: [
        {
          source_line_number: null,
          column: null,
          message: envelope,
          code: typeof data.extra?.code === 'string' ? data.extra.code : null,
        },
      ],
      seatLimitOnly: false,
    }
  }

  return undefined
}

/**
 * The run a 409 says already exists for these bytes, when the same file was sent again
 * asking for a different `suppress_invite`. The first import exists and cannot be
 * re-decided, so the only useful answer is to point at it.
 */
export function getExistingRunId(error: unknown): number | undefined {
  const fetchError = asFetchError(error)
  if (!fetchError || fetchError.status !== StatusCodes.CONFLICT)
    return undefined
  const runId = (fetchError.data.extra as { run_id?: unknown } | undefined)
    ?.run_id
  return typeof runId === 'number' ? runId : undefined
}
